import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import {
  CadsGlobalToastService,
  CadsMenuRowItemComponent,
  CadsMenuSectionComponent,
  type CadsMenuRowItemConfig,
} from '@brightside/angular-cads';
import { take } from 'rxjs';

import { COMMUNICATIONS_PREFERENCES_I18N } from '../constants/communications-preferences-i18n.constants';
import { ClientPreferencesService } from '../client-preferences/client-preferences.service';
import {
  CLIENT_PREFERENCE_KEYS,
  type ClientMarketingPreferenceKey,
  type ClientPreferenceItem,
} from '../models/client-preferences.models';

/** CADS Communications preferences section for the Settings tab. */
@Component({
  selector: 'brightside-web-settings-communications-section',
  imports: [CadsMenuSectionComponent, CadsMenuRowItemComponent],
  template: `
    @if (showSection()) {
      <cads-menu-section [config]="sectionConfig()">
        @if (showSmsRow()) {
          <cads-menu-row-item
            [config]="smsRowConfig()"
            (toggleChanged)="_onToggle(CLIENT_PREFERENCE_KEYS.smsMarketingConsent, $event)"
          />
        }
        @if (showEmailRow()) {
          <cads-menu-row-item
            [config]="emailRowConfig()"
            (toggleChanged)="_onToggle(CLIENT_PREFERENCE_KEYS.emailMarketingConsent, $event)"
          />
        }
      </cads-menu-section>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './settings-communications-section.component.scss',
})
export class SettingsCommunicationsSectionComponent implements OnInit {
  private readonly _clientPreferences = inject(ClientPreferencesService);
  private readonly _translate = inject(TranslateService);
  private readonly _toast = inject(CadsGlobalToastService);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly CLIENT_PREFERENCE_KEYS = CLIENT_PREFERENCE_KEYS;

  private readonly _loadState = signal<'idle' | 'loaded' | 'error'>('idle');
  private readonly _items = signal<ClientPreferenceItem[]>([]);
  private readonly _values = signal<Partial<Record<ClientMarketingPreferenceKey, boolean>>>({});
  private readonly _pendingKeys = signal<ReadonlySet<ClientMarketingPreferenceKey>>(new Set());

  readonly showSection = computed(
    () => this._loadState() === 'loaded' && (this.showSmsRow() || this.showEmailRow()),
  );

  readonly showSmsRow = computed(
    () =>
      this._loadState() === 'loaded' &&
      this._clientPreferences.findPreference(
        this._items(),
        CLIENT_PREFERENCE_KEYS.smsMarketingConsent,
      ) != null,
  );

  readonly showEmailRow = computed(
    () =>
      this._loadState() === 'loaded' &&
      this._clientPreferences.findPreference(
        this._items(),
        CLIENT_PREFERENCE_KEYS.emailMarketingConsent,
      ) != null,
  );

  readonly sectionConfig = computed(() => ({
    title: this._translate.instant(COMMUNICATIONS_PREFERENCES_I18N.heading),
  }));

  readonly smsRowConfig = computed(() =>
    this._toggleRowConfig(
      CLIENT_PREFERENCE_KEYS.smsMarketingConsent,
      COMMUNICATIONS_PREFERENCES_I18N.smsUpdates,
    ),
  );

  readonly emailRowConfig = computed(() =>
    this._toggleRowConfig(
      CLIENT_PREFERENCE_KEYS.emailMarketingConsent,
      COMMUNICATIONS_PREFERENCES_I18N.emailUpdates,
    ),
  );

  private _toggleRowConfig(
    key: ClientMarketingPreferenceKey,
    titleKey: string,
  ): CadsMenuRowItemConfig {
    return {
      density: 'compact',
      menuRowHeading: {
        title: this._translate.instant(titleKey),
        size: 'small',
      },
      menuRowTrailing: {
        kind: 'toggle',
        isOn: this._values()[key] ?? false,
      },
    };
  }

  ngOnInit(): void {
    this._clientPreferences
      .getPreferences()
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (items) => {
          this._items.set(items);
          this._values.set(this._valuesFromItems(items));
          this._loadState.set('loaded');
        },
        error: () => {
          this._loadState.set('error');
        },
      });
  }

  protected _onToggle(key: ClientMarketingPreferenceKey, next: boolean): void {
    if (this._pendingKeys().has(key)) {
      return;
    }
    const prior = this._values()[key] ?? false;
    this._values.update((current) => ({ ...current, [key]: next }));
    this._pendingKeys.update((current) => new Set([...current, key]));

    this._clientPreferences
      .updatePreferences([{ key, booleanValue: next }])
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (items) => {
          this._items.update((current) => this._mergePreferenceItems(current, items));
          this._values.set(this._valuesFromItems(this._items()));
          this._clearPendingKey(key);
          this._toast.success(
            this._translate.instant(COMMUNICATIONS_PREFERENCES_I18N.patchSuccess),
          );
        },
        error: () => {
          this._values.update((current) => ({ ...current, [key]: prior }));
          this._clearPendingKey(key);
          this._toast.error(
            this._translate.instant(COMMUNICATIONS_PREFERENCES_I18N.patchError),
          );
        },
      });
  }

  private _clearPendingKey(key: ClientMarketingPreferenceKey): void {
    this._pendingKeys.update((current) => {
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  }

  private _valuesFromItems(
    items: ClientPreferenceItem[],
  ): Partial<Record<ClientMarketingPreferenceKey, boolean>> {
    const values: Partial<Record<ClientMarketingPreferenceKey, boolean>> = {};
    for (const key of Object.values(CLIENT_PREFERENCE_KEYS)) {
      const item = this._clientPreferences.findPreference(items, key);
      if (item?.booleanValue !== undefined) {
        values[key] = item.booleanValue;
      }
    }
    return values;
  }

  private _mergePreferenceItems(
    current: ClientPreferenceItem[],
    updates: ClientPreferenceItem[],
  ): ClientPreferenceItem[] {
    const merged = [...current];
    for (const update of updates) {
      const index = merged.findIndex((item) => item.key === update.key);
      if (index >= 0) {
        merged[index] = { ...merged[index], ...update };
      } else {
        merged.push(update);
      }
    }
    return merged;
  }
}
